/**
 * @fileoverview A journal of notes, for a plant or a whole bed: the latest note (or all of
 * them), the box to write one in, its quick pills, and edit and delete.
 *
 * Shaped for two places. On a card the notes are what is being read, so the
 * box is a button under them; on the notes screen Edit plant opens, the box
 * stays open at the top and the list scrolls under it. One component for both,
 * so writing a note works the same wherever it happens.
 *
 * Where: PlantCard's Notes tray (CardTiles, tray mode), LocationCard (a bed's journal, with a location
 * subject) and PlantFormDialog's notes screen (screen).
 * API: GET /notes/:plantObjId or /locations/:id/journal; POST to the same;
 * PUT /notes/:noteId or /locations/:id/journal/:noteId; DELETE
 * /notes/:plantObjId/:noteId or /locations/:id/journal/:noteId. Query key
 * ['notes', kind, id]; a plant's change is written into the plants cache
 * too (applyNotesToPlantsCache), and any change marks the season brief
 * stale (seasonWritten, W24 Job H), so the diary on Seasons shows it.
 * Settings: NOTES_FOLDED_SHOWN (config/tunables/plantList); the
 * gardener's note shortcuts (QuickNoteChips).
 * (and PATCH /notes/:plantObjId/:noteId/public, a saved Plant Doctor
 * checkup's Hide from my public page / Show, on a public plant only).
 * Words (en/es): plants.notes.* (add, addFirst, edit, delete, save,
 * showAll, showLess, saveFailed, deleteFailed, publicShown, publicHidden,
 * publicHideShort, publicHide, publicShow, publicFailed, deleteTitle, deleteKeep, deleteConfirm), sitewide.cancel,
 * sitewide.saving and sitewide.deleting (Button loading).
 * B111: every foot is the shared ActionRow (Cancel left, the action right, 44px): editing a note in place, the delete question
 * and (through NoteBox) the new-note box. Deleting is tracked on its own state, so it never lights the new-note box's Save.
 * Watch out: a note's bin asks in place first ("Delete this note?", Keep it left, Delete right, B111) and deletes only on Delete; photos' words are left
 * out of the journal (the server marks them belongsToPhoto). A change that
 * failed on our side says the standard sentence (D31, lib/serverFailure),
 * with the support address as a link.
 *
 * @module NotesSection
 */

import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Plus, Trash2, Pencil, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { formatDate, parseDate, fromServerStamp } from '@/shared/utils/dateUtils';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { QuickNoteChips } from '@/shared/components/QuickNoteChips';
import { NoteBox } from './NoteBox';
import { withShortcut } from '@/shared/lib/noteShortcut';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { applyNotesToPlantsCache } from '../lib/plantNotesCache';
import { seasonWritten } from '@/shared/lib/briefFreshness';
import { plantSubject, type Subject } from '@/shared/api/subject';
import {
  getNotesFor, createNoteFor, updateNoteFor, deleteNoteFor, setNoteHiddenFromPublic, journalNotes, type Note,
} from '@/shared/api/notes';
import { NOTES_FOLDED_SHOWN } from '@/config/tunables/plantList';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';

/** Whose journal this is, where it is shown, and what it may change. */
interface NotesSectionProps {
  /** The plant whose journal this is; or give a subject, for a location's. */
  plantObjId?: number;
  /** Whose journal this is when it is not a plant's (a bed's). Wins over plantObjId. */
  subject?: Subject;
  /** Told after any change, for a caller that keeps its own copy of the notes. */
  onChanged?: () => void;
  /** Notes already in the plants payload, so the card has something at once. */
  initialNotes?: Note[];
  /** Drop the quick chips where space is tight, as in a dialog. */
  showQuickNotes?: boolean;
  /**
   * Show the journal without offering to change it.
   *
   * An archived plant keeps its notes — that is most of why archiving is not
   * deleting — but writing new ones to a plant you have taken out of the
   * garden is either a mistake or an argument that it was never archived.
   */
  readOnly?: boolean;
  /**
   * The journal as a screen of its own, as Edit plant opens it: the box to
   * write in stays open at the top with its quick notes, and every note is
   * listed under it, scrolling on its own. On the card the box is a button
   * under the latest note, because there the card is what is being read.
   */
  screen?: boolean;
  /**
   * The plant is public. A note saved from a Plant Doctor checkup then says
   * whether it shows on the public pages and can be hidden or shown; without
   * this the row is not drawn.
   */
  plantPublic?: boolean;
  /** B136: drawn as a tray under the plant card's tiles (the Notes tile): inset to the card's column, the latest note, the box to write in. */
  tray?: boolean;
}

/**
 * The plant's journal: what is there, and every way to change it.
 *
 * The API has had create, read, update and delete since the beginning and the
 * app called none of them — the card showed whichever note arrived with the
 * plant and offered no way to write another.
 *
 * Deliberately plain: no microphone and no rich text. Every phone keyboard has
 * dictation already, and it will be the native one in the packaged apps.
 *
 * @param props - NotesSectionProps.
 */
export const NotesSection: React.FC<NotesSectionProps> = ({
  plantObjId, subject: subjectProp, onChanged, initialNotes, showQuickNotes = true, readOnly = false, screen = false, plantPublic = false, tray = false,
}) => {
  /** Whose journal: the subject given, or else the plant. */
  const subject = React.useMemo(() => subjectProp ?? plantSubject(plantObjId ?? 0), [subjectProp, plantObjId]);
  const { reactTranslate: t } = useTranslation();
  const limits = useGrowLimits();
  const queryClient = useQueryClient();

  /** The box for a new note is open (always, on the notes screen). */
  const [isWriting, setIsWriting] = React.useState(screen);
  /** The new note's words. */
  const [draft, setDraft] = React.useState('');
  /** The note being edited, or null. */
  const [editingId, setEditingId] = React.useState<number | null>(null);
  /** Its words as they are being changed. */
  const [editDraft, setEditDraft] = React.useState('');
  /** A save or another change (not a delete) is on its way. */
  const [busy, setBusy] = React.useState(false);
  /** The note whose bin was tapped: the delete question shows on it, or null. */
  const [confirmingId, setConfirmingId] = React.useState<number | null>(null);
  /** A delete is on its way (its own flag, so only the Delete the gardener tapped waits, never the new-note box's Save). */
  const [deleting, setDeleting] = React.useState(false);
  /** Every note, not only the latest NOTES_FOLDED_SHOWN (always, on the notes screen). */
  const [showAll, setShowAll] = React.useState(screen);
  /** Why the last change failed. */
  const [error, setError] = React.useState<string | null>(null);
  /** The new note's box, focused as it opens. */
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);

  /** Every note, newest first: GET /notes/:plantObjId, or /locations/:id/journal. */
  const { data: notes = [], refetch } = useQuery({
    queryKey: ['notes', subject.kind, subject.id],
    queryFn: () => getNotesFor(subject),
    initialData: initialNotes,
  });


  // Focus on open so the keyboard comes up without a second tap — the whole
  // point is that this happens standing in a garden.
  // Not on arriving at the notes screen, though: the box is open there from
  // the start, and a keyboard coming up would hide the notes someone may have
  // come to read.
  const wasWriting = React.useRef(isWriting);
  React.useEffect(() => {
    if (isWriting && !wasWriting.current) inputRef.current?.focus();
    wasWriting.current = isWriting;
  }, [isWriting]);

  /** Reads the notes again after a change, brings the plants payload's copy in step, and marks the season diary stale. */
  const refresh = async (): Promise<void> => {
    const { data: fresh } = await refetch();
    seasonWritten(queryClient);

    // The card reads its notes from the plants payload, so that copy is now
    // out of date. Writing the rows in costs no request; see the helper.
    if (fresh && subject.kind === 'plant') applyNotesToPlantsCache(queryClient, subject.id, fresh);
    onChanged?.();

  };

  /**
   * Runs one change, keeping the failure visible rather than swallowing it:
   * the server's refusal where it gave one, else our sentence. Axios's own
   * message ("Request failed with status code 500") used to be shown.
   *
   * @param work - The change.
   * @param failureKey - The words for a failure the server did not explain.
   * @param setWaiting - Which waiting flag to raise while the change is on its way (default: the save flag).
   */
  const run = async (work: () => Promise<void>, failureKey: string, setWaiting: (waiting: boolean) => void = setBusy): Promise<void> => {
    setWaiting(true);
    setError(null);
    try {
      await work();
      await refresh();
    } catch (err) {
      setError(errorMessage(err, t(failureKey)));
    } finally {
      setWaiting(false);
    }
  };

  /** Files the box's note, then empties it (and closes it, on a card). */
  const handleSave = async (): Promise<void> => {
    const content = draft.trim();
    if (!content) return;
    await run(async () => {
      await createNoteFor(subject, content);
      setDraft('');
      setIsWriting(screen);
    }, 'plants.notes.saveFailed');
  };

  /**
   * A shortcut puts its own wording in the box rather than filing it.
   *
   * It used to save on the tap, which was quick but final: "Watered" went into
   * the journal with no chance to make it "Watered, about ten minutes" or to
   * take it back. Composing is what the photo dialog does, and one behaviour
   * for one pill is worth more than the saved tap.
   *
   * Taps accumulate, so three pills make one sentence rather than three notes.
   *
   * @param label - The pill's words.
   */
  const handleQuick = (label: string): void => {
    setError(null);
    /** The box's text with the pill's wording added. */
    const append = (current: string): string => withShortcut(current, label);

    // A pill belongs to whichever note is open. Mid-edit it used to start a
    // second note underneath the one being edited, leaving two boxes on screen
    // and no way to tell which the pill had gone into.
    if (editingId !== null) {
      setEditDraft(append);
      return;
    }

    setIsWriting(true);
    setDraft(append);
    inputRef.current?.focus();
  };

  /**
   * Saves the note being edited and closes its box.
   *
   * @param noteId - The note.
   */
  const handleEditSave = async (noteId: number): Promise<void> => {
    const content = editDraft.trim();
    if (!content) return;
    await run(async () => {
      await updateNoteFor(subject, noteId, content);
      setEditingId(null);
      setEditDraft('');
    }, 'plants.notes.saveFailed');
  };

  /**
   * Deletes a note, once the gardener has answered the question the bin asked; the question closes either way.
   *
   * @param noteId - The note.
   */
  const handleDelete = async (noteId: number): Promise<void> => {
    await run(() => deleteNoteFor(subject, noteId), 'plants.notes.deleteFailed', setDeleting);
    setConfirmingId(null);
  };

  /**
   * Hides a saved checkup note from the public pages, or shows it again.
   *
   * @param note - A note saved from a Plant Doctor checkup.
   */
  const handleTogglePublic = async (note: Note): Promise<void> => {
    await run(async () => {
      await setNoteHiddenFromPublic(subject.id, note.note_id, !note.hidden_from_public);
    }, 'plants.notes.publicFailed');
  };

  /**
   * The journal is the plant's notes, which is every note that is not a
   * photo's.
   */
  const cardNotes = React.useMemo(() => journalNotes(notes), [notes]);

  /** The notes drawn: all, or the latest few. */
  const visible = showAll ? cardNotes : cardNotes.slice(0, NOTES_FOLDED_SHOWN);
  /** How many are folded away, for "Show all". */
  const remaining = cardNotes.length - visible.length;

  /** The notes, each with its edit and delete. */
  const list = (
    <>
      {visible.map((note) => (
        <div key={note.note_id} className="note-paper group rounded-sm bg-muted/50 p-3">
          <div className="note-paper-row mb-1 flex items-center justify-between gap-2">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <MessageSquare className="h-3 w-3 shrink-0" aria-hidden="true" />
              {/* The stamp is UTC: read in the gardener's own zone, so an evening note keeps its day. */}
              {formatDate(fromServerStamp(note.timestamp) ?? parseDate(note.timestamp))}
            </span>
            {/* On the notes screen the box above is always open, so it cannot
                be what hides these, and they stay in sight: there is no hover
                on a phone, and that screen is where notes are looked after. */}
            {!readOnly && editingId === null && confirmingId !== note.note_id && (!isWriting || screen) && (
              <span className={cn(
                'flex shrink-0 items-center gap-1',
                !screen && 'opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100'
              )}>
                <button
                  type="button"
                  onClick={() => { setEditingId(note.note_id); setEditDraft(note.content); }}
                  disabled={busy}
                  aria-label={t('plants.notes.edit')}
                  className="rounded-sm p-1 text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingId(note.note_id)}
                  disabled={busy || deleting}
                  aria-label={t('plants.notes.delete')}
                  className="rounded-sm p-1 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
            )}
          </div>

          {editingId === note.note_id ? (
            <div className="space-y-2">
              <textarea
                aria-label={t('plants.notes.edit')}
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value.slice(0, limits.NOTE_MAX_LENGTH))}
                maxLength={limits.NOTE_MAX_LENGTH}
                rows={3}
                className="w-full rounded-md border border-input bg-card p-2 text-sm outline-none focus:border-primary"
              />
              <ActionRow onCancel={() => { setEditingId(null); setEditDraft(''); }} disabled={busy}>
                <Button size="lg" onClick={() => handleEditSave(note.note_id)} loading={busy} disabled={!editDraft.trim()}>
                  {t('plants.notes.save')}
                </Button>
              </ActionRow>
            </div>
          ) : (
            <p className="whitespace-pre-wrap text-sm">{note.content}</p>
          )}

          {/* The bin asks here, on the note itself: Keep it on the left, Delete on the right. */}
          {confirmingId === note.note_id && editingId === null && (
            <div className="mt-2 space-y-2 border-t border-border pt-2">
              <p className="text-sm font-medium">{t('plants.notes.deleteTitle')}</p>
              <ActionRow onCancel={() => setConfirmingId(null)} cancelLabel={t('plants.notes.deleteKeep')} disabled={deleting}>
                <Button variant="destructive" size="lg" onClick={() => { void handleDelete(note.note_id); }} loading={deleting} busy="deleting">
                  {t('plants.notes.deleteConfirm')}
                </Button>
              </ActionRow>
            </div>
          )}

          {/* A saved checkup on a public plant shows on its public pages unless hidden. */}
          {plantPublic && !readOnly && subject.kind === 'plant' && note.checkup_id && editingId !== note.note_id && (
            <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-2 text-xs text-muted-foreground">
              <span className="flex min-w-0 items-center gap-1.5">
                {note.hidden_from_public
                  ? <EyeOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  : <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                {note.hidden_from_public ? t('plants.notes.publicHidden') : t('plants.notes.publicShown')}
              </span>
              {/* One tap either way: Hide while shown, Show while hidden. */}
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px] shrink-0"
                aria-label={note.hidden_from_public ? undefined : t('plants.notes.publicHide')}
                onClick={() => { void handleTogglePublic(note); }}
                disabled={busy}
              >
                {note.hidden_from_public ? t('plants.notes.publicShow') : t('plants.notes.publicHideShort')}
              </Button>
            </div>
          )}
        </div>
      ))}

      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {t('plants.notes.showAll', { count: remaining })}
        </button>
      )}
      {showAll && !screen && cardNotes.length > NOTES_FOLDED_SHOWN && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {t('plants.notes.showLess')}
        </button>
      )}
    </>
  );

  /** The box for a new note (or the button that opens it), its pills and any failure. */
  const composer = (
    <>
      {/* Exactly one box is open at a time. Editing a note used to leave the
          add-a-note button sitting under it, so a tap could start a second
          note beneath the one being edited with nothing to say which was
          which. */}
      {readOnly ? null : editingId !== null ? null : isWriting ? (
        <NoteBox
          ref={inputRef}
          value={draft}
          onChange={setDraft}
          onSave={() => { void handleSave(); }}
          onCancel={() => { setIsWriting(screen); setDraft(''); setError(null); }}
          busy={busy}
        />
      ) : (
        <button
          type="button"
          onClick={() => setIsWriting(true)}
          disabled={busy}
          className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline disabled:opacity-50"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {/* The journal's own notes: words on a photo are not in it. */}
          {cardNotes.length === 0 ? t('plants.notes.addFirst') : t('plants.notes.add')}
        </button>
      )}

      {/* One tap, no typing — but only while there is a box for them to fill.
          They used to sit under every card all the time, so ten plants meant
          ten rows of pills nobody had asked for, between the journal and the
          next plant. They belong to the box: they appear with it and go away
          with it. Always last, so the box is directly above its own
          shortcuts, which is the order the photo dialog uses. */}
      {showQuickNotes && !readOnly && (isWriting || editingId !== null) && !(screen && editingId !== null) && (
        <QuickNoteChips onPick={handleQuick} disabled={busy} className="pt-0.5" />
      )}

      {error && <p className="text-sm text-destructive"><FailureText text={error} /></p>}
    </>
  );

  if (screen) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col gap-3">
        <div className="shrink-0 space-y-2">{composer}</div>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">{list}</div>
      </div>
    );
  }

  return (
    <div className={cn('w-full space-y-2', tray && 'px-4 pb-1')}>
      {list}
      {composer}
    </div>
  );
};
